import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { SketchOrderStatus } from "../../lib/sketchOrderStatuses";
import SketchOrderCardHeader from "../SketchOrderCardHeader";
import SketchOrderCardDetails from "../SketchOrderCardDetails/SketchOrderCardDetails";
import SketchOrderCardCustomerInformation from "../SketchOrderCardCustomerInformation";
import SketchOrderCardLocation from "../SketchOrderCardLocation";
import SketchOrderCardActions from "../SketchOrderCardActions";
import SketchOrderAssignments from '../SketchOrderAssignments/SketchOrderAssigments';
import { Tabs, Tab } from '@material-ui/core';
import TabPanel from '../TabPanel';

import SketchOrderCardRevisionNotes from '../SketchOrderCardRevisionNotes/SketchOrderCardRevisionNotes';

const style = () => ({
    root: {
        maxWidth: 700,
        margin: 'auto',
    },
});

export const SketchOrderCard = (props) => {
    const { sketchOrder: { revisionNotes } } = props;

    const isReviewer = props.currentUserRoles.isSketchOsAdmin || props.currentUserRoles.isAdmin;
    const sketchOrderIsNotInTheQueue = props.sketchOrder.sketchOrderStatus !== SketchOrderStatus.Pending &&
        props.sketchOrder.sketchOrderStatus !== SketchOrderStatus.Queued;
    
        const [currentTab, setCurrentTab] = useState(0);
    
        const handleTabChange = (event, newValue) => {
            setCurrentTab(newValue);
        }

        useEffect(() => {
            if (revisionNotes?.resourceList.length > 0) {
                setCurrentTab(1);
            }
        }, [revisionNotes])

    return (
        <Paper className={props.classes.root}>
            <SketchOrderCardHeader
                sketchOrder={props.sketchOrder}
                isFetching={props.isFetching}
                project={props.project}
                showViewProjectButton={props.showViewProjectButton}
                authorizedToOpenDrawing={props.authorizedToOpenDrawing}
                currentUserRoles={props.currentUserRoles}
                onDeleteProjectButtonClick={props.onDeleteProjectButtonClick}
                currentUserName={props.currentUserName}
            />
            <SketchOrderAssignments
                sketchTech={props.sketchOrder.sketchTech}
                reviewer={props.sketchOrder.reviewer}
            />

            <Tabs
                value={currentTab}
                indicatorColor='primary'
                textColor='primary'
                onChange={handleTabChange}>
                    <Tab label='Order Details' />
                    {revisionNotes?.resourceList && revisionNotes.resourceList.length > 0 && <Tab label='Revision Notes' />}
            </Tabs>
            <TabPanel selectedTab={currentTab} index={0}>
                <SketchOrderCardDetails
                    sketchOrder={props.sketchOrder}
                    projectDrawingSummary={props.projectDrawingSummary}
                    onSketchOrderChange={props.onSketchOrderChange}
                    sketchOrderTiers={props.sketchOrderTiers}
                    showProjectName={props.showProjectName}
                    showCost={props.showCost}
                    showLinksToSupportDashboard={props.showLinksToSupportDashboard}
                    showReportType={props.showReportType}                  
                    canEditInternalNotes={props.canEditInternalNotes}
                    canEditProjectName={props.canEditProjectName}
                    canEditReportType={props.canEditReportType}
                    canEditTier={props.canEditTier}
                    canEditCost={props.canEditCost}
                    canEditFreeOrder={props.canEditFreeOrder}
                    freeOrder={props.freeOrder}
                    onFreeOrderChange={props.onFreeOrderChange}
                    reportTypeChanged={props.reportTypeChanged}
                    onReportTypeChange={props.onReportTypeChange}
                />
                <SketchOrderCardCustomerInformation
                    sketchOrder={props.sketchOrder}
                    showOrganizationName={props.showOrganizationName}
                    showRequester={props.showRequester}
                    showOwner={props.showOwner}
                    showLinksToSupportDashboard={props.showLinksToSupportDashboard}
                    canEditOwner={props.canEditOwner}
                    onOwnerChange={props.onOwnerChange}
                    organizationsUsers={props.organizationsUsers}
                    onSearchUsers={props.onSearchUsers}
                />
                {
                    (sketchOrderIsNotInTheQueue || isReviewer) &&
                    <SketchOrderCardLocation
                        sketchOrder={props.sketchOrder}
                        onSketchOrderChange={props.onSketchOrderChange}
                        canEditAddress={props.canEditAddress}
                        canEditLocation={props.canEditLocation}
                        onLocationChange={props.onLocationChange}
                    />
                }
                <SketchOrderCardActions
                    sketchOrder={props.sketchOrder}
                    onUpdateClick={props.onUpdateClick}
                    isFetching={props.isFetching}
                />
            </TabPanel>
            <TabPanel selectedTab={currentTab} index={1}>
                <SketchOrderCardRevisionNotes revisionNotes={revisionNotes} />
            </TabPanel>
        </Paper>
    );
};

SketchOrderCard.propTypes = {
    isFetching: PropTypes.bool.isRequired,
    sketchOrder: PropTypes.shape({
        id: PropTypes.number,
        projectName: PropTypes.string,
        sketchOrderStatus: PropTypes.string,
        sketchReportType: PropTypes.string,
        requestedDate: PropTypes.string,
        completedDate: PropTypes.string,
        notes: PropTypes.string,
        address: PropTypes.string,
        city: PropTypes.string,
        region: PropTypes.string,
        postCode: PropTypes.string,
        country: PropTypes.string,
        cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sketchOrderTierId: PropTypes.number,
        projectId: PropTypes.string,
        metalRoofPanelSize: PropTypes.number,
        organization: PropTypes.shape({
            name: PropTypes.string,
        }),
        requester: PropTypes.shape({
            userName: PropTypes.string,
        }),
        owner: PropTypes.shape({
            userName: PropTypes.string,
        }),
        sketchTech: PropTypes.shape({
            userName: PropTypes.string,
        }),
        reviewer: PropTypes.shape({
            userName: PropTypes.string,
        }),
        usedNearmapImage: PropTypes.bool,
        location: PropTypes.shape({
            coordinates: PropTypes.arrayOf(PropTypes.number)
        }),
        sketchOptions: PropTypes.arrayOf(PropTypes.string),
        freeOrder: PropTypes.bool,
        sketchOrderGroup: PropTypes.shape({
            mapMarkerPoints: PropTypes.arrayOf(PropTypes.shape({
                ordinal: PropTypes.number,
                point: PropTypes.shape({
                    coordinates: PropTypes.arrayOf(PropTypes.number),
                }),                
            })),
        }),
    }).isRequired,
    sketchOrderTiers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        sketchOrderTierDefinition: PropTypes.shape({
            minimumValue: PropTypes.number.isRequired,
            maximumValue: PropTypes.number.isRequired,
            sketchOrderTierDefinition: PropTypes.string.isRequired,
        }).isRequired
    })).isRequired,
    project: PropTypes.shape({
        id: PropTypes.string,
        hasDrawing: PropTypes.bool,
        isFetching: PropTypes.bool,
    }),
    onUpdateClick: PropTypes.func.isRequired,
    onSketchOrderChange: PropTypes.func.isRequired,
    classes: PropTypes.shape({
        root: PropTypes.string.isRequired,
    }),
    showViewProjectButton: PropTypes.bool,
    showProjectName: PropTypes.bool,
    showOrganizationName: PropTypes.bool,
    showRequester: PropTypes.bool,
    showOwner: PropTypes.bool,
    showCost: PropTypes.bool,
    showLinksToSupportDashboard: PropTypes.bool,
    showReportType: PropTypes.bool,
    canCompleteSketchOrder: PropTypes.bool,
    canEditAddress: PropTypes.bool,
    canEditReportType: PropTypes.bool,
    canEditOwner: PropTypes.bool,
    canEditProjectName: PropTypes.bool,
    canEditCost: PropTypes.bool,
    canEditFreeOrder: PropTypes.bool,
    canEditTier: PropTypes.bool,
    canEditInternalNotes: PropTypes.bool,
    onOwnerChange: PropTypes.func,
    organizationsUsers: PropTypes.arrayOf(PropTypes.shape({
        userId: PropTypes.number,
        userName: PropTypes.string,
    })),
    onSearchUsers: PropTypes.func.isRequired,
    canEditLocation: PropTypes.bool,
    onLocationChange: PropTypes.func.isRequired,
    selectedLocation: PropTypes.shape({
        coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    authorizedToOpenDrawing: PropTypes.bool.isRequired,
    currentUserRoles: PropTypes.shape({
        isSketchOsAdmin: PropTypes.bool.isRequired,
        isAdmin: PropTypes.bool.isRequired,
    }).isRequired,
    projectDrawingSummary: PropTypes.shape({
        numberOfFacets: PropTypes.number,
        actualSquares: PropTypes.number,
    }).isRequired,
    onFreeOrderChange: PropTypes.func.isRequired,
    reportTypeChanged: PropTypes.bool,
    onReportTypeChange: PropTypes.func.isRequired,
    onDeleteProjectButtonClick: PropTypes.func.isRequired,
    currentUserName: PropTypes.string.isRequired,
};

SketchOrderCard.defaultProps = {
    project: {
        isFetching: true,
    },
    sketchOrder: {},
    showViewProjectButton: false,
    showOrganizationName: false,
    showRequester: false,
    showOwner: false,
    showCost: false,
    showLinksToSupportDashboard: false,
    showReportType: false,
    canCompleteSketchOrder: false,
    canEditAddress: false,
    canEditReportType: false,
    canEditOwner: false,
    canEditProjectName: false,
    canEditCost: false,
    canEditTier: false,
    canEditLocation: false,
    canEditInternalNotes: false,
    canEditFreeOrder: false,
    reportTypeChanged: false,
};

export default withStyles(style)(SketchOrderCard);
